import {useEffect, useMemo} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row} from "antd";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {StatusLabel} from "~/components";
import {
	InputField,
	InputPriceField,
	SelectField,
	GroupRadioField,
	SelectUserAll,
	TextAreaField
} from "~/components/Forms";
import {useUtilities} from "~/hooks";
import useGroup from "~/hooks/useGroup";
import {getGroupSelectOption} from "~/utils";
import {
	voucherTypeOptions,
	dayOfMonthOptions,
	activeOptions,
	costTypeOptions,
	paymentOptions
} from "../../constants";
import {
	slipTypeOptions,
	calcTypeOptions,
	objectOptions,
	defaultCalcBySlipType
} from "~/features/SalarySlip/constants";

const objectTypeOptions = [
	{ value: 'employee', label: 'Nhân viên' },
	{ value: 'group', label: 'Phòng' },
];

function RecurringVoucherFormAddEdit({item, receiptCategories = {}, expenseCategories = {}, onHandleSubmit}) {

	const isEdit = Boolean(item?.id);

	const {utilities} = useUtilities();

	const companyOptions = useMemo(() => {
		return Object.entries(utilities.company || {}).map(([key, value]) => ({
			value: key,
			label: <StatusLabel small type={value.color}>{value.title}</StatusLabel>
		}));
	}, [utilities.company]);

	const bankAccountOptions = useMemo(() => {
		return Object.entries(utilities.bankAccounts || {}).map(([key, value]) => ({
			value: key,
			label: value.title,
		}));
	}, [utilities.bankAccounts]);

	const toGroupOptions = (groups) => {
		if (!groups) return [];
		return Object.entries(groups).map(([, value]) => ({ value: value.id, label: value.name }));
	};

	const orgGroups = useGroup();

	const phongOptions = useMemo(() => {
		return Object.values(getGroupSelectOption(orgGroups || [], 'Chọn phòng'));
	}, [orgGroups]);

	// Mảng options ổn định cho SelectUserAll — tránh DebounceSelect reset kết quả tìm kiếm mỗi lần re-render
	const userOptions = useMemo(() => [], []);

	const initialValues = {
		name: '',
		voucherType: 'expense',
		dayOfMonth: 1,
		active: 1,
		// thu / chi
		categoryId: '',
		costType: 'incidental',
		objectType: 'employee',
		groupId: '',
		// chung
		price: 0,
		companyId: undefined,
		paymentMethod: 'cod',
		bankAccount: '',
		userId: '',
		note: '',
		// lương
		object: 'employee',
		slipType: 'advance',
		calcType: 'minus',
	}

	const validationSchema = Yup.object().shape({
		name: Yup.string().required('Bạn chưa nhập tên mẫu'),
		voucherType: Yup.string().required('Bạn chưa chọn loại phiếu'),
		dayOfMonth: Yup.number().typeError('Ngày không hợp lệ').required('Bạn chưa chọn ngày chạy').min(1).max(31),
		price: Yup.number().typeError('Số tiền không hợp lệ').required('Số tiền không được để trống').min(1, 'Số tiền phải lớn hơn 0'),
		categoryId: Yup.mixed().test('require-category', 'Bạn chưa chọn loại phiếu', function (value) {
			if (this.parent.voucherType !== 'salary') {
				return value !== undefined && value !== null && value !== '';
			}
			return true;
		}),
		userId: Yup.mixed().test('require-user', 'Bạn chưa chọn nhân viên', function (value) {
			if (this.parent.voucherType === 'salary' && this.parent.object === 'employee') {
				return value !== undefined && value !== null && value !== '';
			}
			return true;
		}),
		groupId: Yup.mixed().test('require-group', 'Bạn chưa chọn phòng', function (value) {
			if (this.parent.voucherType === 'expense' && (this.parent.objectType === 'group' || this.parent.costType === 'bonus')) {
				return value !== undefined && value !== null && value !== '';
			}
			return true;
		}),
	})

	const { control, handleSubmit, setValue, formState: { isSubmitting, errors }, reset } = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	const voucherType = useWatch({ control, name: 'voucherType' });
	const salaryObject = useWatch({ control, name: 'object' });
	const expObjectType = useWatch({ control, name: 'objectType' });
	const costType = useWatch({ control, name: 'costType' });
	const paymentMethod = useWatch({ control, name: 'paymentMethod' });

	const isSalary = voucherType === 'salary';
	const isExpense = voucherType === 'expense';
	const isBonus = costType === 'bonus';

	const categoryOptions = useMemo(() => {
		return toGroupOptions(voucherType === 'receipt' ? receiptCategories : expenseCategories);
	}, [voucherType, receiptCategories, expenseCategories]);

	// Thưởng -> bắt buộc đối tượng là Phòng
	useEffect(() => {
		if(isExpense && isBonus) {
			setValue('objectType', 'group');
		}
	}, [isExpense, isBonus, setValue]);

	useEffect(() => {
		if(item?.id) {
			const p = item.payload || {};
			initialValues.name = item.name;
			initialValues.voucherType = item.voucherType;
			initialValues.dayOfMonth = item.dayOfMonth;
			initialValues.active = item.active;
			initialValues.price = p.price || 0;
			initialValues.companyId = p.companyId ? String(p.companyId) : undefined;
			initialValues.paymentMethod = p.paymentMethod || 'cod';
			initialValues.bankAccount = p.bankAccount || '';
			initialValues.userId = p.userId || '';
			initialValues.note = p.note || '';
			if(item.voucherType === 'salary') {
				initialValues.object = p.object || 'employee';
				initialValues.slipType = p.slipType || 'advance';
				initialValues.calcType = p.calcType || 'minus';
			} else {
				initialValues.categoryId = p.categoryId || '';
				initialValues.costType = p.costType || 'incidental';
				initialValues.objectType = p.objectType || 'employee';
				initialValues.groupId = p.groupId ? String(p.groupId) : '';
			}
		}
		reset(initialValues);
	}, [item]);

	const onSubmit = async (data) => {
		let payload;
		if (data.voucherType === 'salary') {
			payload = {
				object: data.object,
				slipType: data.slipType,
				calcType: data.calcType,
				userId: data.userId,
				companyId: data.companyId,
				price: data.price,
				paymentMethod: data.paymentMethod,
				bankAccount: data.bankAccount,
				note: data.note,
			};
		} else {
			payload = {
				categoryId: data.categoryId,
				price: data.price,
				companyId: data.companyId,
				costType: data.costType,
				paymentMethod: data.paymentMethod,
				bankAccount: data.bankAccount,
				userId: data.userId,
				note: data.note,
			};
			if (data.voucherType === 'expense') {
				payload.objectType = data.costType === 'bonus' ? 'group' : data.objectType;
				payload.groupId = data.groupId;
			}
		}
		await onHandleSubmit({
			name: data.name,
			voucherType: data.voucherType,
			dayOfMonth: data.dayOfMonth,
			active: data.active,
			payload,
		}, isEdit);
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<p className="heading">Cài đặt lịch</p>
			<Controller control={control} name="name" render={({field}) => (
				<InputField label="Tên mẫu phiếu" errors={errors} {...field}/>
			)}/>
			<Row gutter={10}>
				<Col className="gutter-row" span={8}>
					<Controller control={control} name="voucherType" render={({ field }) => (
						<SelectField label="Loại phiếu" options={voucherTypeOptions} errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={8}>
					<Controller control={control} name="dayOfMonth" render={({ field }) => (
						<SelectField label="Ngày chạy hằng tháng" options={dayOfMonthOptions} errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={8}>
					<Controller control={control} name="active" render={({ field }) => (
						<SelectField label="Trạng thái" options={activeOptions} errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>

			<p className="heading">Nội dung phiếu</p>
			{isSalary ? (
				<>
					<Row gutter={10}>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="slipType" render={({ field }) => (
								<SelectField label="Loại phiếu lương" options={slipTypeOptions} errors={errors} {...field}
									onChange={(value) => {
										field.onChange(value);
										const def = defaultCalcBySlipType[value];
										if (def) setValue('calcType', def);
									}}
								/>
							)}/>
						</Col>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="calcType" render={({ field }) => (
								<GroupRadioField label="Loại tính" options={calcTypeOptions} errors={errors} {...field}/>
							)}/>
						</Col>
					</Row>
					<Controller control={control} name="object" render={({ field }) => (
						<GroupRadioField label="Đối tượng" options={objectOptions} errors={errors} {...field}/>
					)}/>
					{salaryObject === 'employee' ? (
						<Controller control={control} name="userId" render={({ field }) => (
							<SelectUserAll label="Nhân viên" options={userOptions} errors={errors} {...field} />
						)}/>
					) : (
						<Controller control={control} name="companyId" render={({ field }) => (
							<SelectField label="Công ty" options={companyOptions} errors={errors} {...field}/>
						)}/>
					)}
				</>
			) : (
				<>
					<Row gutter={10}>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="costType" render={({ field }) => (
								<SelectField label="Loại chi phí" options={costTypeOptions} errors={errors} {...field}/>
							)}/>
						</Col>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="categoryId" render={({ field }) => (
								<SelectField label={voucherType === 'receipt' ? 'Loại phiếu thu' : 'Loại phiếu chi'} options={categoryOptions} errors={errors} {...field}/>
							)}/>
						</Col>
					</Row>
					<Controller control={control} name="companyId" render={({ field }) => (
						<SelectField label="Công ty" options={companyOptions} errors={errors} {...field}/>
					)}/>
					{isExpense ? (
						isBonus ? (
							<Controller control={control} name="groupId" render={({ field }) => (
								<SelectField label="Phòng (bắt buộc cho thưởng)" options={phongOptions} errors={errors} {...field}/>
							)}/>
						) : (
							<>
								<Controller control={control} name="objectType" render={({ field }) => (
									<GroupRadioField label="Đối tượng" options={objectTypeOptions} errors={errors} {...field}/>
								)}/>
								{expObjectType === 'group' ? (
									<Controller control={control} name="groupId" render={({ field }) => (
										<SelectField label="Phòng" options={phongOptions} errors={errors} {...field}/>
									)}/>
								) : (
									<Controller control={control} name="userId" render={({ field }) => (
										<SelectUserAll label="Đối tượng (tuỳ chọn)" options={userOptions} errors={errors} {...field} />
									)}/>
								)}
							</>
						)
					) : (
						<Controller control={control} name="userId" render={({ field }) => (
							<SelectUserAll label="Đối tượng (tuỳ chọn)" options={userOptions} errors={errors} {...field} />
						)}/>
					)}
				</>
			)}

			<Controller control={control} name="price" render={({field}) => (
				<InputPriceField label="Số tiền" errors={errors} {...field}/>
			)}/>

			<p className="heading">Thanh toán</p>
			<Controller control={control} name="paymentMethod" render={({ field }) => (
				<GroupRadioField label="Phương thức thanh toán" options={paymentOptions} errors={errors} {...field}/>
			)}/>
			{paymentMethod === 'bank' && (
				<Controller control={control} name="bankAccount" render={({ field }) => (
					<SelectField label="Tài khoản ngân hàng" options={bankAccountOptions} errors={errors} {...field}/>
				)}/>
			)}
			<Controller control={control} name="note" render={({ field }) => (
				<TextAreaField label="Ghi chú" errors={errors} {...field} />
			)}/>
			<hr />
			<div className="form-group d-flex justify-content-end modal-bottom">
				<Button primary type="submit">{isEdit ? 'Cập nhật' : 'Thêm'} phiếu định kỳ</Button>
			</div>
		</form>
	)
}

export default RecurringVoucherFormAddEdit;
